import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

export type ConsentStatus = 'granted' | 'denied' | 'pending';

@Injectable({
    providedIn: 'root',
})
export class ConsentService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly CONSENT_KEY = 'cookie_consent';

    // List of GDPR-regulated countries (EU, EEA, UK, CH)
    private readonly GDPR_COUNTRIES = [
        'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', // EU
        'IS', 'LI', 'NO', // EEA
        'GB', // UK
        'CH'  // Switzerland
    ];

    private consentSubject = new BehaviorSubject<ConsentStatus>(this.getInitialConsent());
    consent$: Observable<ConsentStatus> = this.consentSubject.asObservable();

    private requiresConsentSubject = new BehaviorSubject<boolean>(false);
    requiresConsent$: Observable<boolean> = this.requiresConsentSubject.asObservable();

    constructor() {

        this.initializeConsent();
    }

    private getInitialConsent(): ConsentStatus {
        if (isPlatformBrowser(this.platformId)) {
            const saved = localStorage.getItem(this.CONSENT_KEY);
            if (saved === 'granted' || saved === 'denied') {
                return saved;
            }
        }
        return 'pending';
    }

    private async initializeConsent() {
        if (!isPlatformBrowser(this.platformId)) return;

        const current = this.consentSubject.value;
        if (current !== 'pending') return;

        try {
            // Check if user is in a GDPR country
            const isGdpr = await this.checkIfGdprRegion();

            // Re-check status in case it changed during the fetch
            if (this.consentSubject.value !== 'pending') return;

            if (!isGdpr) {
                // If not in a GDPR region, grant consent automatically
                this.grantConsent();
            } else {
                // If in a GDPR region and status is pending, we explicitly need the banner
                this.requiresConsentSubject.next(true);
            }
        } catch (error) {
            // Fallback: stay in 'pending' and show the banner if geo-check fails
            console.warn('Geolocation check failed, falling back to manual consent', error);
            this.requiresConsentSubject.next(true);
        }
    }


    private async checkIfGdprRegion(): Promise<boolean> {
        try {
            // 1. Detect local development
            if (isPlatformBrowser(this.platformId) &&
                (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
                console.info('[ConsentService] Local development detected, skipping geo-check (default: non-GDPR).');
                return false; // In development, assume non-GDPR to keep banner hidden unless testing
            }

            // 2. Try Cloudflare's built-in trace endpoint
            const response = await fetch('/cdn-cgi/trace');

            if (!response.ok) {
                throw new Error(`Cloudflare trace returned ${response.status}`);
            }

            const text = await response.text();

            // Extract the 'loc=' line which contains the country code
            const lines = text.split('\n');
            const locLine = lines.find((line) => line.trim().startsWith('loc='));
            const countryCode = locLine ? locLine.split('=')[1].trim() : null;

            if (!countryCode) return true; // Safety default: treat as GDPR if we can't tell

            return this.GDPR_COUNTRIES.includes(countryCode);
        } catch (error) {
            console.warn('[ConsentService] Geo-location check failed, falling back to strict consent.', error);
            return true; // Safety default: treat as GDPR if API fails
        }
    }



    grantConsent(): void {
        this.updateConsent('granted');
    }

    denyConsent(): void {
        this.updateConsent('denied');
    }

    private updateConsent(status: ConsentStatus): void {
        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(this.CONSENT_KEY, status);
        }
        this.consentSubject.next(status);
    }

    get currentStatus(): ConsentStatus {
        return this.consentSubject.value;
    }
}

